import Vue from 'vue';
import moment from 'moment';
import { requestSchool } from '@/axios';

function useState() {
  return {
    assignments: [],
    testsLoading: {},
    testsUpdatesLoading: {},
    tickTestTimer: 0,
  };
}

function getAssignmentTimer(assignment) {
  const runningReport = assignment.reports?.find(r => !r.finishDate);
  const { startDate } = runningReport;

  if (assignment.duration) {
    const maxFinishDate = moment(startDate).add(assignment.duration, 'minutes');
    const diff = moment().diff(maxFinishDate, 'ms');
    return moment.utc().startOf('day').subtract(diff < 0 ? diff : 0, 'ms');
  }

  const diff = moment().diff(startDate, 'ms');
  return moment.utc().startOf('day').add(diff, 'ms');
}

const getters = {
  assignments: state => state.assignments,
  runningAssignments(state) {
    return state.assignments.filter(a => a.reports?.some(r => (r.startDate && !r.finishDate)));
  },
  assignmentsTimers(state, gettersArg) {
    return state.tickTestTimer
      && gettersArg.runningAssignments.reduce((timersObj, assignment) => {
        const assignmentTimer = getAssignmentTimer(assignment);
        return Object.assign(timersObj, { [assignment._id]: assignmentTimer });
      }, {});
  },
  currentPeriodAssignments(state) {
    return state.assignments.filter((assignment) => {
      const now = moment();
      return now.isBetween(assignment.startDate, assignment.dueDate);
    });
  },
  overdueTimerAssignments(state, gettersArg) {
    return gettersArg.runningAssignments.filter((assignment) => {
      const assignmentTimer = gettersArg.assignmentsTimers[assignment._id];
      const { hours, minutes, seconds, milliseconds } = assignmentTimer.toObject();
      return (hours + minutes + seconds + milliseconds) === 0;
    });
  },
  testsLoading(state) {
    return state.testsLoading;
  },
  testsUpdatesLoading(state) {
    return state.testsUpdatesLoading;
  },
};

const actions = {
  loadAssignments({ commit }) {
    return requestSchool
      .get('/assignment/child/all')
      .then(({ data }) => {
        commit('SET_ASSIGNMENTS', data.assignments);
        return data.assignments;
      });
  },
  loadAssignment({ commit }, { assignmentId }) {
    return requestSchool.get(`/assignment/child/all/${assignmentId}`)
      .then(({ data }) => {
        commit('SET_ASSIGNMENT', data.assignment);
        return data.assignment;
      });
  },
  startAssignmentTest({ commit }, { assignmentId }) {
    commit('SET_TEST_LOADING', { assignmentId, value: true });
    return requestSchool.post(`/assignment/${assignmentId}/test/start`)
      .then((res) => {
        const { report } = res.data;
        commit('SET_ASSIGNMENT_REPORT', { assignmentId, report });
        return res.data;
      })
      .finally(() => commit('SET_TEST_LOADING', { assignmentId, value: false }));
  },
  updateAssignmentTest({ commit }, { assignmentId, answers }) {
    commit('SET_TEST_UPDATE_LOADING', { assignmentId, value: true });
    return requestSchool.put(`/assignment/${assignmentId}/test/update`, { answers })
      .then((res) => {
        const { report } = res.data;
        commit('SET_ASSIGNMENT_REPORT', { assignmentId, report });
        return report;
      })
      .finally(() => commit('SET_TEST_UPDATE_LOADING', { assignmentId, value: false }));
  },
  finishAssignmentTest({ commit }, { assignmentId, answers }) {
    commit('SET_TEST_LOADING', { assignmentId, value: true });
    return requestSchool.post(`/assignment/${assignmentId}/test/finish`, { answers })
      .then((res) => {
        const { report } = res.data;
        commit('SET_ASSIGNMENT_REPORT', { assignmentId, report });
        return res.data;
      })
      .finally(() => commit('SET_TEST_LOADING', { assignmentId, value: false }));
  },
  tickTestsTimer({ commit }) {
    commit('UPDATE_TEST_TIMER');
  },
};

const mutations = {
  SET_ASSIGNMENTS(state, assignments) {
    state.assignments = assignments;
  },
  SET_ASSIGNMENT(state, assignment) {
    const idx = state.assignments.findIndex(a => a._id === assignment._id);
    if (idx !== -1) {
      state.assignments.splice(idx, 1, assignment);
    } else {
      state.assignments.unshift(assignment);
    }
  },
  UPDATE_TEST_TIMER(state) {
    state.tickTestTimer += 1;
  },
  SET_ASSIGNMENT_REPORT(state, { assignmentId, report }) {
    const assignment = state.assignments.find(a => a._id === assignmentId);
    if (!assignment.reports?.length) {
      assignment.reports = [report];
      return;
    }
    const reportIdx = assignment.reports.findIndex(r => r._id === report._id);
    if (reportIdx === -1) {
      assignment.reports.push(report);
    } else {
      assignment.reports.splice(reportIdx, 1, report);
    }
  },
  SET_TEST_LOADING(state, { assignmentId, value }) {
    if (value) {
      Vue.set(state.testsLoading, assignmentId, value);
    } else {
      Vue.delete(state.testsLoading, assignmentId);
    }
  },
  SET_TEST_UPDATE_LOADING(state, { assignmentId, value }) {
    if (value) {
      Vue.set(state.testsUpdatesLoading, assignmentId, value);
    } else {
      Vue.delete(state.testsUpdatesLoading, assignmentId);
    }
  },
};

export default {
  namespaced: true,
  state: useState,
  getters,
  actions,
  mutations,
};
